import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, PieChart, Pie, Cell,
} from 'recharts';
import {
  Download, MessageCircle, UserCheck, Eye, TrendingUp, Sparkles, Globe2, Loader2,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API = process.env.REACT_APP_BACKEND_URL;
const RANGES = [
  { id: 'today', label: 'Today' },
  { id: '7d',    label: 'Last 7 Days' },
  { id: '30d',   label: 'Last 30 Days' },
  { id: 'all',   label: 'All Time' },
];
const PIE_COLORS = ['#FFD700', '#C7A7FF', '#FF6B35', '#22C55E', '#3B82F6', '#EC4899', '#06B6D4', '#A855F7'];

function Metric({ icon: Icon, label, value, accent = 'gold' }) {
  return (
    <div className="rounded-2xl bg-bg-card border border-border-gold/30 p-5" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between mb-3">
        <span className={`w-9 h-9 rounded-lg bg-${accent}/10 border border-${accent}/30 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 text-${accent}`} />
        </span>
      </div>
      <p className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">{value?.toLocaleString?.() ?? value}</p>
      <p className="text-xs text-text-muted mt-1 leading-tight">{label}</p>
    </div>
  );
}

export default function ConversionsAdminPage() {
  const [range, setRange] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) return;
    setLoading(true); setError(null);
    axios.get(`${API}/api/admin/conversions/analytics?range=${range}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Failed to load analytics'))
      .finally(() => setLoading(false));
  }, [range, token]);

  if (!token) return <Navigate to="/admin" replace />;

  const o = data?.overview || {};
  const funnel = data?.funnel || {};
  const funnelArr = [
    { stage: 'Blog View',        v: funnel.blog_view || 0 },
    { stage: 'Profile Visit',    v: funnel.profile_visit || 0 },
    { stage: 'WhatsApp',         v: funnel.whatsapp || 0 },
    { stage: 'Consultation',     v: funnel.consultation || 0 },
  ];

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary">
      <Header />

      <section className="pt-24 pb-12">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Top bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div>
              <span className="text-xs tracking-[0.3em] text-gold/70 uppercase">Admin</span>
              <h1 className="text-3xl sm:text-4xl font-heading font-bold mt-1">Conversion Analytics</h1>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center bg-bg-card border border-gold/30 rounded-full p-1">
                {RANGES.map(r => (
                  <button key={r.id} onClick={() => setRange(r.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${range === r.id ? 'bg-gold text-bg-primary' : 'text-text-muted hover:text-gold'}`}
                    data-testid={`range-${r.id}`}>{r.label}</button>
                ))}
              </div>
              <a href={`${API}/api/admin/conversions/export.csv?range=${range}`}
                 target="_blank" rel="noopener noreferrer"
                 onClick={(e) => {
                   e.preventDefault();
                   // Fetch with auth header, then trigger download
                   fetch(`${API}/api/admin/conversions/export.csv?range=${range}`, {
                     headers: { Authorization: `Bearer ${token}` },
                   }).then(r => r.blob()).then(blob => {
                     const url = URL.createObjectURL(blob);
                     const a = document.createElement('a');
                     a.href = url; a.download = `conversions-${range}.csv`;
                     a.click(); URL.revokeObjectURL(url);
                   });
                 }}
                 className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gold text-bg-primary text-xs font-semibold hover:bg-yellow-400 transition"
                 data-testid="export-csv">
                <Download className="w-3.5 h-3.5" /> Export CSV
              </a>
            </div>
          </div>

          {loading && <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>}
          {error && <div className="rounded-xl bg-red-500/10 border border-red-500/40 p-4 text-red-300">{error}</div>}

          {data && (<>
            {/* Overview Metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-8">
              <Metric icon={TrendingUp}  label="Total Events"        value={o.total_events} />
              <Metric icon={MessageCircle} label="WhatsApp Clicks"   value={o.whatsapp_clicks} />
              <Metric icon={UserCheck}   label="Consultation Clicks" value={o.consultation_clicks} />
              <Metric icon={Eye}         label="Priya Profile Visits" value={o.priya_profile_visits} />
              <Metric icon={Eye}         label="Expert Profile Visits" value={o.expert_profile_visits} />
              <Metric icon={Sparkles}    label="Total Leads"         value={o.total_leads} />
            </div>

            {/* Funnel */}
            <div className="rounded-2xl bg-bg-card border border-gold/20 p-5 sm:p-6 mb-8" data-testid="funnel">
              <h2 className="text-lg font-bold mb-4">Conversion Funnel</h2>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={funnelArr} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#3b2860" />
                  <XAxis type="number" stroke="#888" />
                  <YAxis dataKey="stage" type="category" stroke="#FFD700" width={110} />
                  <Tooltip contentStyle={{ background: '#13091e', border: '1px solid #FFD700' }} />
                  <Bar dataKey="v" fill="#FFD700" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-3 gap-3 mt-3 text-xs text-text-muted">
                {funnelArr.slice(1).map((s, i) => {
                  const prev = funnelArr[i].v || 1;
                  const drop = Math.max(0, 100 - Math.round((s.v / prev) * 100));
                  return (<div key={s.stage} className="text-center">{funnelArr[i].stage} → <b className="text-text-primary">{s.stage}</b>: <span className="text-amber-300">{drop}% drop</span></div>);
                })}
              </div>
            </div>

            {/* Daily Trend */}
            <div className="rounded-2xl bg-bg-card border border-gold/20 p-5 sm:p-6 mb-8">
              <h2 className="text-lg font-bold mb-4">Daily Trend (last 30 days)</h2>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={data.daily_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#3b2860" />
                  <XAxis dataKey="date" stroke="#888" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#888" />
                  <Tooltip contentStyle={{ background: '#13091e', border: '1px solid #FFD700' }} />
                  <Legend />
                  <Line type="monotone" dataKey="events" stroke="#C7A7FF" strokeWidth={2} name="Events" />
                  <Line type="monotone" dataKey="leads"  stroke="#FFD700" strokeWidth={2} name="Leads" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Experts comparison */}
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {Object.entries(data.experts || {}).map(([key, e]) => (
                <div key={key} className="rounded-2xl bg-bg-card border border-gold/20 p-5" data-testid={`expert-${key}`}>
                  <h3 className="text-lg font-bold mb-3">{e.name}</h3>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><p className="text-2xl font-bold text-gold">{e.profile_visits}</p><p className="text-[11px] text-text-muted mt-1">Profile Visits</p></div>
                    <div><p className="text-2xl font-bold text-green-400">{e.whatsapp_clicks}</p><p className="text-[11px] text-text-muted mt-1">WhatsApp</p></div>
                    <div><p className="text-2xl font-bold text-purple-300">{e.consultation_requests}</p><p className="text-[11px] text-text-muted mt-1">Consultations</p></div>
                  </div>
                </div>
              ))}
            </div>

            {/* Top Blogs */}
            <div className="rounded-2xl bg-bg-card border border-gold/20 p-5 sm:p-6 mb-8 overflow-x-auto" data-testid="top-blogs">
              <h2 className="text-lg font-bold mb-4">Top Performing Blogs</h2>
              <table className="w-full text-sm min-w-[700px]">
                <thead className="text-xs text-gold/80 uppercase tracking-wider">
                  <tr className="border-b border-gold/20">
                    <th className="text-left py-2 pr-3">Title</th>
                    <th className="text-left py-2 pr-3">Category</th>
                    <th className="text-right py-2 pr-3">Views</th>
                    <th className="text-right py-2 pr-3">WhatsApp</th>
                    <th className="text-right py-2 pr-3">Consult</th>
                    <th className="text-right py-2 pr-3">Profile</th>
                    <th className="text-right py-2">CR %</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.top_blogs || []).map(b => (
                    <tr key={b.slug} className="border-b border-gold/5 hover:bg-gold/5">
                      <td className="py-2 pr-3"><a href={b.url} target="_blank" rel="noopener noreferrer" className="text-text-primary hover:text-gold">{b.title}</a></td>
                      <td className="py-2 pr-3 text-text-muted">{b.category}</td>
                      <td className="py-2 pr-3 text-right">{b.views}</td>
                      <td className="py-2 pr-3 text-right text-green-400 font-semibold">{b.whatsapp}</td>
                      <td className="py-2 pr-3 text-right text-purple-300">{b.consultation}</td>
                      <td className="py-2 pr-3 text-right">{b.profile_visit}</td>
                      <td className="py-2 text-right text-gold font-semibold">{b.conversion_rate_pct}%</td>
                    </tr>
                  ))}
                  {(!data.top_blogs?.length) && <tr><td colSpan="7" className="py-6 text-center text-text-muted text-xs">No blog conversion data yet for this range.</td></tr>}
                </tbody>
              </table>
            </div>

            {/* Categories + Traffic Source + Country */}
            <div className="grid lg:grid-cols-3 gap-4 mb-8">
              <div className="rounded-2xl bg-bg-card border border-gold/20 p-5" data-testid="categories">
                <h3 className="text-base font-bold mb-3">Category Performance</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={data.categories} dataKey="leads" nameKey="category" outerRadius={80} label={(d) => d.category}>
                      {(data.categories || []).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#13091e', border: '1px solid #FFD700' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="rounded-2xl bg-bg-card border border-gold/20 p-5" data-testid="traffic-sources">
                <h3 className="text-base font-bold mb-3">Traffic Sources</h3>
                <ul className="space-y-1.5 text-sm">
                  {(data.traffic_sources || []).map(s => (
                    <li key={s.source} className="flex justify-between border-b border-gold/10 py-1.5">
                      <span className="text-text-muted">{s.source}</span>
                      <span className="text-text-primary font-semibold">{s.clicks} <span className="text-[10px] text-gold">· {s.leads} leads</span></span>
                    </li>
                  ))}
                  {(!data.traffic_sources?.length) && <li className="text-text-muted text-xs">No traffic data yet</li>}
                </ul>
              </div>

              <div className="rounded-2xl bg-bg-card border border-gold/20 p-5" data-testid="countries">
                <h3 className="text-base font-bold mb-3 flex items-center gap-1.5"><Globe2 className="w-4 h-4 text-gold" /> Countries</h3>
                <ul className="space-y-1.5 text-sm">
                  {(data.countries || []).map(c => (
                    <li key={c.code} className="flex justify-between border-b border-gold/10 py-1.5">
                      <span className="text-text-muted">{c.name} <span className="text-[10px] text-text-muted">({c.code})</span></span>
                      <span className="text-text-primary font-semibold">{c.visits} <span className="text-[10px] text-gold">· {c.leads} leads · {c.conversion_rate_pct}%</span></span>
                    </li>
                  ))}
                  {(!data.countries?.length) && <li className="text-text-muted text-xs">No country data yet</li>}
                </ul>
              </div>
            </div>

            {/* Event Breakdown */}
            <div className="rounded-2xl bg-bg-card border border-gold/20 p-5 mb-8" data-testid="event-breakdown">
              <h2 className="text-lg font-bold mb-3">Event Breakdown</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 text-sm">
                {Object.entries(data.by_event || {}).sort((a,b) => b[1]-a[1]).map(([k,v]) => (
                  <div key={k} className="flex justify-between bg-bg-primary/40 border border-gold/10 rounded-lg px-3 py-2">
                    <span className="text-text-muted text-xs truncate" title={k}>{k}</span>
                    <span className="text-gold font-semibold">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </>)}
        </div>
      </section>

      <Footer />
    </div>
  );
}
